import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from '../api/axios';
import { useUser } from '../context/UserContext';

export default function TicketPurchasePage() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { user } = useUser();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState(1); // 1: Info, 2: Payment Method, 3: Complete
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    nationality: '', // local or international
    paymentMethod: '',
    specialRequests: ''
  });
  
  const [userProfile, setUserProfile] = useState(null);
  const [eventBankInfo, setEventBankInfo] = useState(null);
  const [ticketStatus, setTicketStatus] = useState(null); // 사용자의 티켓 신청 상태
  const [currentTime, setCurrentTime] = useState(new Date()); // ✅ 실시간 카운트다운을 위한 state

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        const response = await axios.get(`/api/events/detail?eventId=${eventId}`);
        setEvent(response.data);
        
        // ✅ Bank 정보 파싱 (MembershipPage와 동일한 로직)
        if (response.data.bankAccount) {
          try {
            const bankParts = response.data.bankAccount.split(' - ');
            if (bankParts.length >= 3) {
              const bankInfo = {
                bank: bankParts[0],
                account: bankParts[1],
                name: bankParts[2]
              };
              setEventBankInfo(bankInfo);
            }
          } catch (error) {
            // Silent fail
          }
        }
        
        // 사용자 정보가 있으면 폼 미리 채우기
        if (user?.isLoggedIn) {
          // 임시 해결책: API로 사용자 프로필 정보 가져오기
          try {
            const profileResponse = await axios.get('/api/users/me');
            const profileData = profileResponse.data;
            setUserProfile(profileData);
            
            setFormData(prev => ({
              ...prev,
              name: user.name || '',
              email: user.email || '',
              phone: profileData?.phone || '',
              nationality: profileData?.nationality || ''
            }));
          } catch (profileError) {
            // 프로필 정보를 가져올 수 없으면 기본 정보만 설정
            setFormData(prev => ({
              ...prev,
              name: user.name || '',
              email: user.email || '',
              phone: '',
              nationality: ''
            }));
          }
        }
        
        // ✅ 사용자의 티켓 신청 상태 확인
        if (user?.isLoggedIn) {
          try {
            const statusResponse = await axios.get(`/api/events/${eventId}/attendance-status`);
            setTicketStatus(statusResponse.data);
          } catch (error) {
            setTicketStatus(null);
          }
        }
      } catch (error) {
        // Silent fail
      } finally {
        setLoading(false);
      }
    };

    fetchEvent();
  }, [eventId, user]);

  // ✅ 실시간 카운트다운을 위한 useEffect
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    
    return () => clearInterval(timer);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // ✅ 버튼 상태 및 텍스트 결정 함수
  const getButtonState = () => {
    if (!ticketStatus) {
      return { text: 'Register', disabled: false, className: 'bg-orange-500 hover:bg-orange-600' };
    }
    
    if (ticketStatus.status === 'PENDING') {
      return { text: 'Wait for Approval', disabled: false, className: 'bg-yellow-500 hover:bg-yellow-600' };
    }
    
    if (ticketStatus.status === 'ATTENDING') {
      return { text: 'View Event Details', disabled: false, className: 'bg-green-500 hover:bg-green-600' };
    }
    
    return { text: 'Register', disabled: false, className: 'bg-orange-500 hover:bg-orange-600' };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // 티켓 구매 API 호출 (결제 로직은 나중에 구현)
      const response = await axios.post(`/api/events/${eventId}/purchase-ticket`, formData);
      
      // Step 3으로 이동
      setCurrentStep(3);
    } catch (error) {
      // ✅ 중복 신청 체크
      if (error.response?.status === 400 && error.response?.data?.error?.includes("이미 이 이벤트에 티켓을 신청하셨습니다")) {
        alert("You have already applied for a ticket to this event. Please wait for approval.");
      } else {
        alert('Failed to purchase ticket: ' + (error.response?.data?.error || error.response?.data?.message || error.message));
      }
    }
  };

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16">
        <div className="text-center">Loading...</div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16">
        <div className="text-center text-red-600">Event not found.</div>
      </div>
    );
  }

  // Early Bird 데드라인 기준으로 가격 결정
  const earlyBirdDeadline = event.earlyBirdDeadline;
  const now = new Date();
  const deadlineDate = earlyBirdDeadline ? new Date(earlyBirdDeadline) : null;
  const isEarlyBirdActive = deadlineDate && now < deadlineDate && event.currentMembers < event.earlyBirdCap;
  
  // 백엔드에서 계산된 currentPrice 사용, 없으면 로컬 계산
  const currentPrice = event.currentPrice || (isEarlyBirdActive ? event.earlyBirdPrice : event.price);

  // ✅ Registration Deadline 카운트다운 계산 (초 포함)
  const registrationDeadline = event?.registrationDeadline;
  const regDeadlineDate = registrationDeadline ? new Date(registrationDeadline) : null;
  const timeUntilDeadline = regDeadlineDate ? regDeadlineDate - currentTime : null;
  
  const getTimeLeft = () => {
    if (!timeUntilDeadline || timeUntilDeadline <= 0) return 'Closed';
    
    const days = Math.floor(timeUntilDeadline / (1000 * 60 * 60 * 24));
    const hours = Math.floor((timeUntilDeadline % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((timeUntilDeadline % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((timeUntilDeadline % (1000 * 60)) / 1000);
    
    if (days > 0) return `${days}d ${hours}h ${minutes}m ${seconds}s`;
    if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
    return `${minutes}m ${seconds}s`;
  };


  // ✅ Capacity Warning 계산
  const shouldShowCapacityWarning = () => {
    if (!event?.showCapacityWarning || !event?.capacityWarningThreshold) return false;
    
    // 멤버십 사용자에게는 경고 메시지 표시하지 않음
    if (user?.status === 'ACTIVE_MEMBER') return false;
    
    const warningBase = event.capacityWarningBase || event.currentAttendees || 0;
    const warningLimit = event.capacityWarningLimit || event.capacity || 0;
    const spotsLeft = warningLimit - warningBase;
    const validSpotsLeft = Math.max(0, spotsLeft);
    
    return validSpotsLeft <= event.capacityWarningThreshold;
  };

  const timeLeft = getTimeLeft();

  // Step 1: Event Information
  const renderStep1 = () => (
    <div className="max-w-2xl mx-auto px-4 py-16">
      <div className="bg-white rounded-lg shadow-lg p-8">
        {/* 이벤트 정보 */}
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-4">
            <span className="bg-orange-100 text-orange-800 px-3 py-1 rounded-full text-sm font-semibold">
              🎫 {event.productType || 'Ticket'}
            </span>
            <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-semibold">
              {event.branch}
            </span>
          </div>
          
          <h1 className="text-3xl font-bold text-gray-900 mb-4">{event.title}</h1>
          
          {/* ✅ FOMO 유발 UI: Registration Deadline 카운트다운 (반응형) */}
          {(
            <div className="mb-6">
              <div className={`inline-flex items-center space-x-3 rounded-full px-8 py-4 text-lg font-bold border-2 ${
                timeLeft === 'Closed' 
                  ? 'bg-gray-200 text-gray-700 border-gray-400' 
                  : 'bg-gradient-to-r from-red-500 to-orange-500 text-white border-red-600 shadow-xl animate-pulse'
              }`}>
                {timeLeft === 'Closed' ? (
                  <>
                    <span className="text-2xl">🔒</span>
                    <span>Registration Closed</span>
                  </>
                ) : (
                  <>
                    <span className="animate-bounce text-2xl">⚡</span>
                    {/* 모바일: 줄바꿈, PC: 한줄 */}
                    <div className="text-center md:text-left">
                      <div className="block md:hidden">
                        <div className="text-sm">Registration closes in</div>
                        <div className="text-lg font-black tracking-wider">
                          {timeLeft}
                        </div>
                      </div>
                      <div className="hidden md:block">
                        Registration closes in {timeLeft}
                      </div>
                    </div>
                    <span className="animate-bounce text-2xl">⏰</span>
                  </>
                )}
              </div>
            </div>
          )}

          {/* ✅ FOMO 유발 UI: Capacity Warning */}
          {shouldShowCapacityWarning() && (
            <div className="mb-6 bg-red-50 border-l-4 border-red-400 p-4 rounded-lg">
              <div className="flex items-center">
                <span className="text-red-500 text-xl mr-3">⚠️</span>
                <div>
                  <p className="text-red-800 font-bold text-lg">
                    Hurry up! Only {Math.max(0, (event.capacityWarningLimit || event.capacity || 0) - (event.capacityWarningBase || event.currentAttendees || 0))} spots left!
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* ✅ FOMO 유발 UI: Early Bird 활성화 시 추가 경고 */}
          {isEarlyBirdActive && (
            <div className="mb-6 bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-lg">
              <div className="flex items-center">
                <span className="text-yellow-500 text-xl mr-3">🐦</span>
                <div>
                  <p className="text-yellow-800 font-bold text-lg">
                    Early Bird Special Active! Limited time offer!
                  </p>
                  <p className="text-yellow-600 text-sm">
                    Get {event.earlyBirdPrice} NTD discount! Only {event.earlyBirdCap - event.currentMembers} early bird spots left!
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {/* ✅ 개선된 이벤트 정보 카드 */}
          <div className="bg-gradient-to-br from-indigo-50 via-white to-purple-50 p-8 rounded-2xl border border-indigo-100 mb-8 shadow-lg">
            <div className="space-y-6">
              {/* 날짜 정보 */}
              <div className="flex items-center space-x-4">
                <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-4 rounded-full shadow-md">
                  <span className="text-white text-2xl">📅</span>
                </div>
                <div>
                  <p className="text-sm text-gray-500 font-medium uppercase tracking-wide">Date</p>
                  <p className="text-xl font-bold text-gray-900">
                    {new Date(event.eventDateTime).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    })}
                  </p>
                </div>
              </div>

              {/* 시간 정보 */}
              <div className="flex items-center space-x-4">
                <div className="bg-gradient-to-br from-purple-500 to-purple-600 p-4 rounded-full shadow-md">
                  <span className="text-white text-2xl">🕒</span>
                </div>
                <div>
                  <p className="text-sm text-gray-500 font-medium uppercase tracking-wide">Time</p>
                  <p className="text-xl font-bold text-gray-900">
                    {(() => {
                      const startDate = new Date(event.eventDateTime);
                      const startTime = startDate.toLocaleTimeString('en-US', {
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: false
                      });
                      
                      if (event.endTime) {
                        return `${startTime} – ${event.endTime}`;
                      }
                      return startTime;
                    })()}
                  </p>
                </div>
              </div>

              {/* 장소 정보 - 클릭 가능 */}
              <div className="flex items-center space-x-4">
                <div className="bg-gradient-to-br from-green-500 to-green-600 p-4 rounded-full shadow-md">
                  <span className="text-white text-2xl">📍</span>
                </div>
                <div className="text-left w-full">
                  <p className="text-sm text-gray-500 font-medium uppercase tracking-wide">Location</p>
                  <button
                    onClick={() => {
                      // Google Maps로 이동
                      const encodedLocation = encodeURIComponent(event.location);
                      window.open(`https://www.google.com/maps/search/?api=1&query=${encodedLocation}`, '_blank');
                    }}
                    className="text-xl font-bold text-blue-600 hover:text-blue-800 hover:underline transition-colors cursor-pointer text-left"
                  >
                    {event.location} 🗺️
                  </button>
                </div>
              </div>

              {/* 가격 정보 */}
              <div className="flex items-start space-x-4 md:space-x-6">
                <div className="bg-gradient-to-br from-orange-500 to-orange-600 p-3 md:p-4 rounded-full shadow-md flex-shrink-0">
                  <span className="text-white text-xl md:text-2xl">💰</span>
                </div>
                <div className="w-full min-w-0">
                  <p className="text-sm text-gray-500 font-medium uppercase tracking-wide mb-3">Price</p>
                  
                  {event.isEarlyBirdActive ? (
                    <div className="flex items-baseline gap-3 flex-wrap">
                      <span className="text-2xl md:text-3xl font-bold text-green-600">
                        NT${currentPrice?.toLocaleString()}
                      </span>
                      <span className="text-lg md:text-xl text-gray-500 line-through">
                        NT${event.price?.toLocaleString()}
                      </span>
                    </div>
                  ) : (
                    <span className="text-2xl md:text-3xl font-bold text-orange-600">
                      {currentPrice === 0 ? 'FREE' : `NT$${currentPrice?.toLocaleString()}`}
                    </span>
                  )}
                </div>
              </div>

            </div>
          </div>

          {/* ✅ Early Bird 할인 배지 */}
          {event.isEarlyBirdActive && (
            <div className="mb-6">
              <div className="bg-gradient-to-r from-green-100 to-green-200 text-green-800 px-6 py-3 rounded-full text-center shadow-sm border border-green-300">
                <span className="text-lg font-bold">
                  🐦 Early Bird -{event.price - event.earlyBirdPrice} NTD OFF!
                </span>
              </div>
            </div>
          )}

          {/* ✅ Early Bird 정보 별도 칸 */}
          {event.isEarlyBirdActive && (
            <div className="bg-gradient-to-r from-yellow-50 to-orange-50 p-6 rounded-2xl border border-yellow-200 mb-6 shadow-lg">
              <div className="mb-4">
                <p className="text-lg font-bold text-orange-800">Early Bird Special Active!</p>
                <p className="text-sm text-orange-600">Limited time offer - Don't miss out!</p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {event.earlyBirdEndDate && (
                  <div className="bg-white p-4 rounded-xl border border-yellow-200">
                    <div className="flex items-center space-x-3">
                      <span className="text-yellow-600 text-xl">⏰</span>
                      <div>
                        <p className="text-sm text-gray-600 font-medium">Early Bird Ends</p>
                        <p className="text-lg font-bold text-gray-900">
                          {new Date(event.earlyBirdEndDate).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                
                {event.earlyBirdCapacity && (
                  <div className="bg-white p-4 rounded-xl border border-yellow-200">
                    <div className="flex items-center space-x-3">
                      <span className="text-orange-600 text-xl">🎫</span>
                      <div>
                        <p className="text-sm text-gray-600 font-medium">Early Bird Spots Left</p>
                        <p className="text-2xl font-bold text-orange-600">
                          {Math.max(0, event.earlyBirdCapacity - (event.currentMembers || 0))}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Event Description */}
          {event.description && (
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-medium text-gray-900 mb-2">Event Description</h3>
              <p className="text-gray-700 whitespace-pre-wrap">{event.description}</p>
            </div>
          )}
        </div>

        {/* Nationality Selection */}
        <div className="mb-8">
          <label className="block text-sm font-medium text-gray-700 mb-4">
            {user?.isLoggedIn && userProfile?.nationality ? 'Your Nationality' : 'Where are you from?'} *
          </label>
          
          {user?.isLoggedIn && userProfile?.nationality ? (
            // Membership 가입자: 자동으로 nationality 사용
            <div className="mb-4 p-4 bg-green-50 rounded-lg border border-green-200">
              <div className="flex items-center">
                <span className="text-green-600 mr-2">✓</span>
                <div>
                  <p className="text-sm font-medium text-green-800">
                    Using your membership profile nationality
                  </p>
                  <p className="text-sm text-green-700 mt-1">
                    {formData.nationality === 'Taiwan' ? '🇹🇼 Taiwan (Local)' : `🌍 ${formData.nationality} (International)`}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            // Guest: 나라 선택 드롭다운
            <div className="space-y-3">
              <select
                name="nationality"
                value={formData.nationality}
                onChange={handleInputChange}
                className="w-full p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-lg"
                required
              >
                <option value="">Select your country</option>
                <option value="Taiwan">🇹🇼 Taiwan</option>
                <option value="South Korea">🇰🇷 South Korea</option>
                <option value="Japan">🇯🇵 Japan</option>
                <option value="China">🇨🇳 China</option>
                <option value="Hong Kong">🇭🇰 Hong Kong</option>
                <option value="Singapore">🇸🇬 Singapore</option>
                <option value="Malaysia">🇲🇾 Malaysia</option>
                <option value="Thailand">🇹🇭 Thailand</option>
                <option value="Vietnam">🇻🇳 Vietnam</option>
                <option value="Philippines">🇵🇭 Philippines</option>
                <option value="Indonesia">🇮🇩 Indonesia</option>
                <option value="India">🇮🇳 India</option>
                <option value="United States">🇺🇸 United States</option>
                <option value="Canada">🇨🇦 Canada</option>
                <option value="United Kingdom">🇬🇧 United Kingdom</option>
                <option value="Germany">🇩🇪 Germany</option>
                <option value="France">🇫🇷 France</option>
                <option value="Spain">🇪🇸 Spain</option>
                <option value="Italy">🇮🇹 Italy</option>
                <option value="Netherlands">🇳🇱 Netherlands</option>
                <option value="Sweden">🇸🇪 Sweden</option>
                <option value="Norway">🇳🇴 Norway</option>
                <option value="Denmark">🇩🇰 Denmark</option>
                <option value="Finland">🇫🇮 Finland</option>
                <option value="Switzerland">🇨🇭 Switzerland</option>
                <option value="Austria">🇦🇹 Austria</option>
                <option value="Belgium">🇧🇪 Belgium</option>
                <option value="Australia">🇦🇺 Australia</option>
                <option value="New Zealand">🇳🇿 New Zealand</option>
                <option value="Brazil">🇧🇷 Brazil</option>
                <option value="Argentina">🇦🇷 Argentina</option>
                <option value="Mexico">🇲🇽 Mexico</option>
                <option value="Chile">🇨🇱 Chile</option>
                <option value="Colombia">🇨🇴 Colombia</option>
                <option value="Peru">🇵🇪 Peru</option>
                <option value="Venezuela">🇻🇪 Venezuela</option>
                <option value="Russia">🇷🇺 Russia</option>
                <option value="Ukraine">🇺🇦 Ukraine</option>
                <option value="Poland">🇵🇱 Poland</option>
                <option value="Czech Republic">🇨🇿 Czech Republic</option>
                <option value="Hungary">🇭🇺 Hungary</option>
                <option value="Romania">🇷🇴 Romania</option>
                <option value="Bulgaria">🇧🇬 Bulgaria</option>
                <option value="Croatia">🇭🇷 Croatia</option>
                <option value="Serbia">🇷🇸 Serbia</option>
                <option value="Slovenia">🇸🇮 Slovenia</option>
                <option value="Slovakia">🇸🇰 Slovakia</option>
                <option value="Estonia">🇪🇪 Estonia</option>
                <option value="Latvia">🇱🇻 Latvia</option>
                <option value="Lithuania">🇱🇹 Lithuania</option>
                <option value="Turkey">🇹🇷 Turkey</option>
                <option value="Israel">🇮🇱 Israel</option>
                <option value="Saudi Arabia">🇸🇦 Saudi Arabia</option>
                <option value="United Arab Emirates">🇦🇪 United Arab Emirates</option>
                <option value="Egypt">🇪🇬 Egypt</option>
                <option value="South Africa">🇿🇦 South Africa</option>
                <option value="Nigeria">🇳🇬 Nigeria</option>
                <option value="Kenya">🇰🇪 Kenya</option>
                <option value="Morocco">🇲🇦 Morocco</option>
                <option value="Tunisia">🇹🇳 Tunisia</option>
                <option value="Algeria">🇩🇿 Algeria</option>
                <option value="Ethiopia">🇪🇹 Ethiopia</option>
                <option value="Ghana">🇬🇭 Ghana</option>
                <option value="Other">🌍 Other</option>
              </select>
            </div>
          )}
        </div>

        <div className="flex gap-4 pt-6">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Go Back
          </button>
          <button
            type="button"
            onClick={() => {
              const buttonState = getButtonState();
              if (buttonState.text === 'View Event Details') {
                // 티켓이 승인된 경우 이벤트 상세 페이지로 이동
                navigate(`/events/${eventId}`);
              } else {
                // 일반적인 경우 결제 방법 선택 단계로 이동
                setCurrentStep(2);
              }
            }}
            disabled={getButtonState().disabled || (!formData.nationality && getButtonState().text === 'Register')}
            className={`flex-1 px-6 py-3 text-white rounded-full transition-all duration-200 font-semibold text-base shadow-md hover:shadow-lg disabled:bg-gray-300 disabled:cursor-not-allowed ${getButtonState().className}`}
          >
            {getButtonState().text === 'Register' && 'Register Now'}
            {getButtonState().text === 'Wait for Approval' && 'Wait for Approval'}
            {getButtonState().text === 'View Event Details' && 'View Event Details'}
          </button>
        </div>
      </div>
    </div>
  );

  // Step 2: Payment Method Selection
  const renderStep2 = () => (
    <div className="max-w-2xl mx-auto px-4 py-16">
      <div className="bg-white rounded-lg shadow-lg p-8">
        <h3 className="text-2xl font-bold text-center mb-8 text-gray-800">Choose Your Payment Method</h3>
        
        <div className="space-y-4 mb-8">
          <div 
            className={`border-2 rounded-xl p-6 cursor-pointer transition-all duration-200 ${
              formData.paymentMethod === 'transfer' 
                ? 'border-blue-500 bg-blue-50 shadow-lg' 
                : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
            }`}
            onClick={() => setFormData(prev => ({ ...prev, paymentMethod: 'transfer' }))}
          >
            <div className="flex items-start space-x-4">
              <div className="text-3xl">💳</div>
              <div className="flex-1">
                <div className="font-semibold text-gray-800 text-lg">Bank Transfer</div>
                <div className="text-sm text-gray-600 mt-1">Secure online payment via bank transfer</div>
                <div className="text-xs text-blue-600 mt-2">Quick and convenient</div>
              </div>
              <div className={`w-5 h-5 rounded-full border-2 ${
                formData.paymentMethod === 'transfer' 
                  ? 'border-blue-500 bg-blue-500' 
                  : 'border-gray-300'
              } flex items-center justify-center`}>
                {formData.paymentMethod === 'transfer' && <div className="w-2 h-2 bg-white rounded-full"></div>}
              </div>
            </div>
          </div>
          
          <div 
            className={`border-2 rounded-xl p-6 cursor-pointer transition-all duration-200 ${
              formData.paymentMethod === 'cash' 
                ? 'border-green-500 bg-green-50 shadow-lg' 
                : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
            }`}
            onClick={() => setFormData(prev => ({ ...prev, paymentMethod: 'cash' }))}
          >
            <div className="flex items-start space-x-4">
              <div className="text-3xl">💵</div>
              <div className="flex-1">
                <div className="font-semibold text-gray-800 text-lg">Cash Payment</div>
                <div className="text-sm text-gray-600 mt-1">Pay in cash at the event</div>
                <div className="text-xs text-green-600 mt-2">No online payment required</div>
              </div>
              <div className={`w-5 h-5 rounded-full border-2 ${
                formData.paymentMethod === 'cash' 
                  ? 'border-green-500 bg-green-500' 
                  : 'border-gray-300'
              } flex items-center justify-center`}>
                {formData.paymentMethod === 'cash' && <div className="w-2 h-2 bg-white rounded-full"></div>}
              </div>
            </div>
          </div>
        </div>

        {/* ✅ Transfer Details (MembershipPage와 동일한 형태) */}
        {formData.paymentMethod === 'transfer' && event && (
          <div className="mt-6 p-6 bg-blue-50 rounded-xl border border-blue-200">
            <div className="text-center mb-4">
              <p className="font-bold text-blue-800 text-lg">📋 Transfer Details</p>
            </div>
            <div className="space-y-2 text-sm bg-white p-4 rounded-lg border border-blue-100">
              <p><strong>Amount:</strong> {currentPrice === 0 ? 'FREE' : `${currentPrice} NTD`}</p>
              {event.bankName && (
                <p><strong>Bank Name:</strong> {event.bankName}</p>
              )}
              {eventBankInfo && (
                <p><strong>Account:</strong> {eventBankInfo.account}</p>
              )}
              {event.accountName && (
                <p><strong>Account Name:</strong> {event.accountName}</p>
              )}
            </div>
            
            {/* ✅ Last 5 digits of bank account 입력 필드 추가 */}
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Last 5 digits of your bank account (for verification):
              </label>
              <input
                type="text"
                name="bankLast5"
                value={formData.bankLast5 || ''}
                onChange={handleInputChange}
                placeholder="Enter last 5 digits"
                maxLength="5"
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
          </div>
        )}

        {/* ✅ Cash Payment Details */}
        {formData.paymentMethod === 'cash' && (
          <div className="mt-6 p-6 bg-green-50 rounded-xl border border-green-200">
            <div className="text-center mb-4">
              <p className="font-bold text-green-800 text-lg">💵 Cash Payment Details</p>
            </div>
            <div className="space-y-2 text-sm bg-white p-4 rounded-lg border border-green-100">
              <p><strong>Amount:</strong> {currentPrice === 0 ? 'FREE' : `${currentPrice} NTD`}</p>
              <p><strong>Payment:</strong> Pay in cash at the event</p>
              <p className="text-green-600 text-xs mt-2">
                💡 No online payment required. Bring exact cash amount to the event.
              </p>
            </div>
          </div>
        )}

        <div className="flex gap-4 pt-6">
          <button
            type="button"
            onClick={() => setCurrentStep(1)}
            className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Back
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!formData.paymentMethod}
            className="flex-1 px-6 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors font-semibold disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            Complete Registration
          </button>
        </div>
      </div>
    </div>
  );

  // Step 3: Complete Registration
  const renderStep3 = () => (
    <div className="max-w-2xl mx-auto px-4 py-16">
      <div className="bg-white rounded-lg shadow-lg p-8 text-center">
        <div className="mb-6">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-2xl">✅</span>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Registration Complete!</h2>
          <p className="text-gray-600">
            Your ticket registration has been submitted successfully.
          </p>
        </div>

        <div className="bg-gray-50 p-4 rounded-lg mb-6 text-left">
          <h3 className="font-medium text-gray-900 mb-2">Registration Details</h3>
          <div className="space-y-1 text-sm text-gray-600">
            <p><strong>Event:</strong> {event.title}</p>
            <p><strong>Name:</strong> {formData.name}</p>
            <p><strong>Email:</strong> {formData.email}</p>
            <p><strong>Nationality:</strong> {formData.nationality === 'Taiwan' ? '🇹🇼 Taiwan (Local)' : `🌍 ${formData.nationality} (International)`}</p>
            <p><strong>Payment Method:</strong> {formData.paymentMethod === 'cash' ? '💵 Cash' : '🏦 Bank Transfer'}</p>
            <p><strong>Amount:</strong> {currentPrice === 0 ? 'FREE' : `NT$${currentPrice?.toLocaleString()}`}</p>
          </div>
        </div>

        <div className="flex gap-4">
          <button
            onClick={() => navigate('/events')}
            className="flex-1 px-6 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors font-semibold"
          >
            Back to Events
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {currentStep === 1 && renderStep1()}
      {currentStep === 2 && renderStep2()}
      {currentStep === 3 && renderStep3()}
    </>
  );
}
